// =============================================================================
// VINUM - App Shell Layout
// Migrated from Flutter AppShell / CustomNavShell with EXACT behavior
// Responsive layout with sidebar (desktop) or bottom nav (mobile)
// =============================================================================

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { ThemeToggle } from '@/components/ui';
import {
  useUIStore,
  useAuthStore,
  useCartStore,
  useFavoritesStore,
  useNotificationStore,
  useNotificationWebSocket,
  useOnboardingStore,
  ROUTES,
  selectIsMobile,
  selectIsDesktop,
  selectIsTablet,
  selectSidebarCollapsed,
  selectCartTotalItems,
  selectFavoritesCount,
  selectHasManagerRole,
  selectIsStaffMember,
  selectHasCustomer,
  selectIsPrivato,
} from '@vinum/shared';
import type { ApiNotification } from '@vinum/shared';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';

// =============================================================================
// Navigation Items
// Exact match with Flutter navigation structure
// =============================================================================

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  requiresManager?: boolean;
  customerOnly?: boolean; // Hide from staff (Manager/Admin)
  requiresWinery?: boolean; // Only show if user has customer (winery) or is staff
  children?: NavItem[];
}

const NAV_ITEMS: NavItem[] = [
  {
    path: ROUTES.HOME,
    label: 'Home',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    path: ROUTES.CATALOG,
    label: 'Catalogo',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    path: ROUTES.FAVORITES,
    label: 'Preferiti',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    // Sezione "Riepilogo": storico Ordini + Scarichi. Visibile a tutti i
    // clienti e allo staff (che dentro le pagine ha la select cliente).
    path: ROUTES.ORDERS,
    label: 'Riepilogo',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    children: [
      {
        path: ROUTES.ORDERS,
        label: 'Ordini',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
      },
      {
        path: ROUTES.SCARICHI,
        label: 'Scarichi',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        ),
        requiresWinery: true, // solo clienti con cantina (ristoranti) + staff
      },
    ],
  },
  {
    path: ROUTES.WINERY,
    label: 'Cantina',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    requiresWinery: true,
  },
  {
    path: ROUTES.NOTIFICATIONS,
    label: 'Notifiche',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    customerOnly: true,
  },
];

// Voce "Profilo" mostrata in fondo alla sidebar (pinned bottom),
// non nel flusso principale di navigazione.
const PROFILE_BOTTOM_ITEM: NavItem = {
  path: ROUTES.PROFILE,
  label: 'Profilo',
  icon: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
};

// Chevron icon for expandable items
const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={cn('w-4 h-4 transition-transform duration-200', expanded && 'rotate-180')}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const WINERY_NAV_ITEMS: NavItem[] = [
  {
    path: ROUTES.STATISTICS,
    label: 'Statistiche',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    requiresWinery: true,
    children: [
      {
        path: ROUTES.STATISTICS_PRODUCTS,
        label: 'Prodotti',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        ),
      },
      // NB: la voce "Vendite" e' stata spostata nel pannello admin come tab
      // di /admin/analytics (un Privato/Azienda non vende, vede solo i propri
      // consumi sotto "Prodotti"). La rotta /statistics/sales continua a
      // funzionare come redirect per i link legacy.
    ],
  },
];

const MANAGER_NAV_ITEMS: NavItem[] = [
  {
    path: ROUTES.ADMIN,
    label: 'Amministrazione',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    requiresManager: true,
  },
];

// =============================================================================
// App Shell Component
// =============================================================================

export const AppShell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useUIStore(selectIsMobile);
  const isTablet = useUIStore(selectIsTablet);
  const isDesktop = useUIStore(selectIsDesktop);
  const sidebarCollapsed = useUIStore(selectSidebarCollapsed);
  // Sidebar visible on both tablet (always collapsed) and desktop
  const showSidebar = isDesktop || isTablet;
  // On tablet, force collapsed sidebar (icon-only)
  const effectiveCollapsed = isTablet || sidebarCollapsed;
  const toggleSidebarCollapsed = useUIStore((state) => state.toggleSidebarCollapsed);
  const setWindowWidth = useUIStore((state) => state.setWindowWidth);
  const hasManagerRole = useAuthStore(selectHasManagerRole);
  const isStaffMember = useAuthStore(selectIsStaffMember);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasCustomer = useAuthStore(selectHasCustomer);
  const isPrivato = useAuthStore(selectIsPrivato);
  const cartItemCount = useCartStore(selectCartTotalItems);
  const favoritesCount = useFavoritesStore(selectFavoritesCount);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const recentNotifications = useNotificationStore((s) => s.recentNotifications);
  const notifLoading = useNotificationStore((s) => s.isLoading);
  const fetchNotifCount = useNotificationStore((s) => s.fetchCount);
  const fetchNotifRecent = useNotificationStore((s) => s.fetchRecent);
  const markNotifRead = useNotificationStore((s) => s.markAsRead);
  const markAllNotifRead = useNotificationStore((s) => s.markAllAsRead);

  const [bellOpen, setBellOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [mobileSidebarAnimating, setMobileSidebarAnimating] = useState(false);
  const [expandedSidebarItems, setExpandedSidebarItems] = useState<Set<string>>(new Set());
  const bellRef = useRef<HTMLDivElement>(null);

  // Mobile sidebar animation
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileSidebarVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMobileSidebarAnimating(true);
        });
      });
    } else {
      setMobileSidebarAnimating(false);
      const timeout = setTimeout(() => setMobileSidebarVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [mobileMenuOpen]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    };
    if (bellOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bellOpen]);

  // WebSocket for real-time notifications
  const showInfoToast = useUIStore((state) => state.showInfoToast);
  useNotificationWebSocket({
    enabled: isAuthenticated,
    onNotification: useCallback((notification: ApiNotification) => {
      showInfoToast(notification.title, notification.body);
    }, [showInfoToast]),
  });

  // Fetch notification count periodically (backup fallback, reduced from 60s to 5min)
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 300_000); // every 5 min (backup)
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotifCount]);

  // Auto-start onboarding tour on first login
  const user = useAuthStore((s) => s.user);
  const startTour = useOnboardingStore((s) => s.startTour);
  useEffect(() => {
    if (user && user.tutorialCompleted === false) {
      const timeout = setTimeout(() => startTour(), 1000);
      return () => clearTimeout(timeout);
    }
  }, [user, startTour]);

  const handleBellClick = useCallback(() => {
    setBellOpen((prev) => {
      if (!prev) fetchNotifRecent();
      return !prev;
    });
  }, [fetchNotifRecent]);

  const handleNotifClick = useCallback((id: string, read: boolean) => {
    if (!read) markNotifRead(id);
  }, [markNotifRead]);

  // Helper: relative time in Italian
  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'adesso';
    if (min < 60) return `${min} min fa`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs} ore fa`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'ieri';
    return `${days}g fa`;
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWindowWidth]);

  // Get filtered nav items based on role
  // - Staff (Manager/Admin) should not see customerOnly items (e.g., "Ordini" customer page)
  // - requiresWinery items only visible if user has customer (winery) or is staff
  // - Manager items are added for users with manager role or higher
  // - Winery items (Statistiche) are visible to anyone with a winery or staff
  // - Privato non vede MAI Cantina / Statistiche, anche se ha un Customer associato
  //   (allineato con il route guard RequireWineryArea)
  const canSeeWinery = (isStaffMember || hasCustomer) && !isPrivato;

  // Filtro applicato sia agli item top-level che ai loro children.
  // Senza il filtro children, una voce come "Vendite" sotto "Statistiche"
  // marcata requiresManager sarebbe visibile anche agli utenti Azienda.
  const isVisibleForRole = (item: NavItem): boolean => {
    if (item.customerOnly && isStaffMember) return false;
    if (item.requiresManager && !isStaffMember) return false;
    if (item.requiresWinery && !canSeeWinery) return false;
    return true;
  };

  const applyChildrenFilter = (item: NavItem): NavItem => {
    if (!item.children || item.children.length === 0) return item;
    return { ...item, children: item.children.filter(isVisibleForRole) };
  };

  const navItems = [
    ...NAV_ITEMS.filter(isVisibleForRole).map(applyChildrenFilter),
    ...(canSeeWinery
      ? WINERY_NAV_ITEMS.filter(isVisibleForRole).map(applyChildrenFilter)
      : []),
    ...(hasManagerRole ? MANAGER_NAV_ITEMS : []),
  ];

  // Collect all paths (including children) for isActive matching
  const allPaths: string[] = navItems.flatMap(item =>
    item.children ? [item.path, ...item.children.map(c => c.path)] : [item.path]
  );

  const isActive = (path: string) => {
    if (path === ROUTES.HOME) {
      return location.pathname === ROUTES.HOME;
    }
    if (!location.pathname.startsWith(path)) return false;
    // Avoid matching a parent route when a more specific child route matches
    return !allPaths.some(
      (other) => other !== path && other.length > path.length && location.pathname.startsWith(other)
    );
  };

  // Check if a parent item or any of its children is active
  const isParentActive = (item: NavItem) => {
    if (location.pathname.startsWith(item.path)) return true;
    return item.children?.some(child => location.pathname.startsWith(child.path)) ?? false;
  };

  // Auto-expand sidebar items when navigating to their children
  useEffect(() => {
    navItems.forEach(item => {
      if (item.children && isParentActive(item)) {
        setExpandedSidebarItems(prev => {
          if (prev.has(item.path)) return prev;
          const next = new Set(prev);
          next.add(item.path);
          return next;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpand = (path: string) => {
    setExpandedSidebarItems(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarVisible && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <div
            className={cn(
              'absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
              mobileSidebarAnimating ? 'opacity-100' : 'opacity-0'
            )}
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Sidebar Panel */}
          <aside
            className={cn(
              'absolute left-0 top-0 h-full w-72 shadow-xl flex flex-col transition-transform duration-300 ease-out',
              mobileSidebarAnimating ? 'translate-x-0' : '-translate-x-full'
            )}
            style={{
              backgroundColor: 'var(--color-card)',
            }}
          >
            {/* Header */}
            <div
              className="h-16 flex items-center justify-between px-4 border-b flex-shrink-0"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <Link to={ROUTES.HOME} className="flex items-center gap-3">
                <img src="/logo.png" alt="VINUM" className="w-9 h-9" />
                <span className="text-xl font-bold text-primary">VINUM</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Chiudi menu"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.path}>
                  {item.children ? (
                    <>
                      {/* Expandable parent */}
                      <button
                        onClick={() => toggleExpand(item.path)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                          isParentActive(item)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        )}
                        style={!isParentActive(item) ? { color: 'var(--color-text-secondary)' } : undefined}
                      >
                        <span className="w-6 h-6 flex-shrink-0">{item.icon}</span>
                        <span className="font-medium text-sm">{item.label}</span>
                        <span className="ml-auto">
                          <ChevronIcon expanded={expandedSidebarItems.has(item.path)} />
                        </span>
                      </button>
                      {/* Children */}
                      {expandedSidebarItems.has(item.path) && (
                        <div className="ml-6 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={cn(
                                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                isActive(child.path)
                                  ? 'bg-primary/10 text-primary'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                              )}
                              style={!isActive(child.path) ? { color: 'var(--color-text-secondary)' } : undefined}
                            >
                              <span className="w-5 h-5 flex-shrink-0">{child.icon}</span>
                              <span className="font-medium text-sm">{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                        isActive(item.path)
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                      )}
                      style={!isActive(item.path) ? { color: 'var(--color-text-secondary)' } : undefined}
                    >
                      <span className="w-6 h-6 flex-shrink-0">{item.icon}</span>
                      <span className="font-medium text-sm">{item.label}</span>
                      {item.path === ROUTES.NOTIFICATIONS && unreadCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                      {item.path === ROUTES.FAVORITES && favoritesCount > 0 && (
                        <span className="ml-auto bg-wine-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                          {favoritesCount > 99 ? '99+' : favoritesCount}
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Footer */}
            <div
              className="border-t p-4 flex-shrink-0 space-y-1"
              style={{ borderColor: 'var(--color-border)' }}
            >
              {/* Profilo */}
              <Link
                to={PROFILE_BOTTOM_ITEM.path}
                data-tour="nav-profilo"
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                  isActive(PROFILE_BOTTOM_ITEM.path)
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
                style={
                  !isActive(PROFILE_BOTTOM_ITEM.path)
                    ? { color: 'var(--color-text-secondary)' }
                    : undefined
                }
              >
                <span className="w-6 h-6 flex-shrink-0">{PROFILE_BOTTOM_ITEM.icon}</span>
                <span className="font-medium text-sm">{PROFILE_BOTTOM_ITEM.label}</span>
              </Link>

              {/* Carrello */}
              <Link
                to={ROUTES.CART}
                data-tour="nav-cart"
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                  'hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-medium text-sm">Carrello</span>
                {cartItemCount > 0 && (
                  <span className="ml-auto bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {cartItemCount}
                  </span>
                )}
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* Sidebar — desktop (collapsible) + tablet (always collapsed) */}
      {showSidebar && (
        <aside
          className={cn(
            'fixed left-0 top-0 h-full',
            'border-r transition-all duration-300 z-40',
            'flex flex-col',
            effectiveCollapsed ? 'w-20' : 'w-64'
          )}
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          {/* Logo */}
          <div
            className="h-16 flex items-center justify-between px-4 border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {!effectiveCollapsed && (
              <Link to={ROUTES.HOME} className="flex items-center gap-3">
                <img src="/logo.png" alt="VINUM" className="w-10 h-10" />
                <span className="text-xl font-bold text-primary">VINUM</span>
              </Link>
            )}
            {/* Hide collapse toggle on tablet (always collapsed there) */}
            {!isTablet && (
              <button
                onClick={toggleSidebarCollapsed}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7m8 14l-7-7 7-7"} />
                </svg>
              </button>
            )}
            {/* Show logo icon on tablet */}
            {isTablet && (
              <Link to={ROUTES.HOME} className="mx-auto">
                <img src="/logo.png" alt="VINUM" className="w-10 h-10" />
              </Link>
            )}
          </div>

          {/* Nav Items */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-tour="sidebar-nav">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.children ? (
                  <>
                    {/* Expandable parent — collapsed: click navigates to first child; expanded: toggle */}
                    {effectiveCollapsed ? (
                      <Link
                        to={item.children[0]?.path ?? item.path}
                        data-tour={`nav-${item.path.replace('/', '') || 'home'}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                          isParentActive(item)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        )}
                        title={item.label}
                      >
                        <span className="flex-shrink-0">{item.icon}</span>
                      </Link>
                    ) : (
                      <>
                        <button
                          data-tour={`nav-${item.path.replace('/', '') || 'home'}`}
                          onClick={() => toggleExpand(item.path)}
                          className={cn(
                            'w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                            isParentActive(item)
                              ? 'bg-primary/10 text-primary'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                          )}
                        >
                          <span className="flex-shrink-0">{item.icon}</span>
                          <span className="font-medium">{item.label}</span>
                          <span className="ml-auto">
                            <ChevronIcon expanded={expandedSidebarItems.has(item.path)} />
                          </span>
                        </button>
                        {expandedSidebarItems.has(item.path) && (
                          <div className="ml-6 mt-1 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={cn(
                                  'flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors text-sm',
                                  isActive(child.path)
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                )}
                              >
                                <span className="flex-shrink-0">{child.icon}</span>
                                <span className="font-medium">{child.label}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    data-tour={`nav-${item.path.replace('/', '') || 'home'}`}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                      isActive(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    <span className="relative flex-shrink-0">
                      {item.icon}
                      {item.path === ROUTES.NOTIFICATIONS && unreadCount > 0 && effectiveCollapsed && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                      {item.path === ROUTES.FAVORITES && favoritesCount > 0 && effectiveCollapsed && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-wine-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {favoritesCount > 9 ? '9+' : favoritesCount}
                        </span>
                      )}
                    </span>
                    {!effectiveCollapsed && <span className="font-medium">{item.label}</span>}
                    {item.path === ROUTES.NOTIFICATIONS && unreadCount > 0 && !effectiveCollapsed && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    {item.path === ROUTES.FAVORITES && favoritesCount > 0 && !effectiveCollapsed && (
                      <span className="ml-auto bg-wine-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                        {favoritesCount > 99 ? '99+' : favoritesCount}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Bottom "Profilo" section — pinned al fondo della sidebar */}
          <div
            className="border-t p-4 flex-shrink-0"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <Link
              to={PROFILE_BOTTOM_ITEM.path}
              data-tour="nav-profilo"
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                isActive(PROFILE_BOTTOM_ITEM.path)
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
              title={effectiveCollapsed ? PROFILE_BOTTOM_ITEM.label : undefined}
            >
              <span className="flex-shrink-0">{PROFILE_BOTTOM_ITEM.icon}</span>
              {!effectiveCollapsed && (
                <span className="font-medium">{PROFILE_BOTTOM_ITEM.label}</span>
              )}
            </Link>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen',
          showSidebar && (effectiveCollapsed ? 'ml-20' : 'ml-64'),
          isMobile && 'pb-20'
        )}
      >
        {/* Top Header */}
        <header
          className="h-16 border-b sticky top-0 z-30"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="h-full flex items-center justify-between px-4 sm:px-6">
            {/* Mobile: Hamburger + Logo */}
            {isMobile && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  data-tour="mobile-menu-button"
                  className="p-2 -ml-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Apri menu"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  </svg>
                </button>
                <Link to={ROUTES.HOME} className="flex items-center gap-2">
                  <img src="/logo.png" alt="VINUM" className="w-8 h-8" />
                  <span className="text-lg font-bold text-primary">VINUM</span>
                </Link>
              </div>
            )}

            {/* Desktop/Tablet: Page title area (spacer) */}
            {showSidebar && <div />}

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notification Bell */}
              <div ref={bellRef} className="relative">
                <button
                  onClick={handleBellClick}
                  className="relative p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Notifiche"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown / Bottom Sheet */}
                {bellOpen && (
                  <>
                    {isMobile ? (
                      /* ── Mobile: Bottom Sheet ── */
                      <div className="fixed inset-0 z-[9999]">
                        {/* Backdrop */}
                        <div
                          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                          onClick={() => setBellOpen(false)}
                        />
                        {/* Sheet */}
                        <div
                          className="absolute bottom-0 left-0 right-0 rounded-t-2xl shadow-xl overflow-hidden animate-slide-up"
                          style={{
                            backgroundColor: 'var(--color-card)',
                            maxHeight: '85vh',
                          }}
                        >
                          {/* Handle bar */}
                          <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                          </div>

                          {/* Header */}
                          <div
                            className="flex items-center justify-between px-5 py-3 border-b"
                            style={{ borderColor: 'var(--color-border)' }}
                          >
                            <h3 className="font-semibold text-base" style={{ color: 'var(--color-text)' }}>Notifiche</h3>
                            <div className="flex items-center gap-3">
                              {unreadCount > 0 && (
                                <button
                                  onClick={() => markAllNotifRead()}
                                  className="text-sm text-primary active:opacity-70"
                                >
                                  Segna tutte lette
                                </button>
                              )}
                              <button
                                onClick={() => setBellOpen(false)}
                                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                style={{ color: 'var(--color-text-secondary)' }}
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* List */}
                          <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 130px)' }}>
                            {notifLoading ? (
                              <div className="flex items-center justify-center py-12">
                                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : recentNotifications.length === 0 ? (
                              <div className="py-12 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                Nessuna notifica
                              </div>
                            ) : (
                              recentNotifications.map((n) => (
                                <button
                                  key={n.id}
                                  onClick={() => handleNotifClick(n.id, n.read)}
                                  className={cn(
                                    'w-full text-left px-5 py-4 border-b transition-colors active:bg-gray-100 dark:active:bg-gray-800/50',
                                    !n.read && 'bg-primary/5'
                                  )}
                                  style={{ borderColor: 'var(--color-border)' }}
                                >
                                  <div className="flex items-start gap-3">
                                    {!n.read && (
                                      <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0" />
                                    )}
                                    <div className={cn('flex-1 min-w-0', n.read && 'ml-5')}>
                                      <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                                        {n.title}
                                      </p>
                                      <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                                        {n.body}
                                      </p>
                                      <p className="text-xs mt-1.5 opacity-50" style={{ color: 'var(--color-text-secondary)' }}>
                                        {timeAgo(n.sentAt)}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>

                          {/* Footer */}
                          {recentNotifications.length > 0 && (
                            <div
                              className="px-5 py-3 border-t safe-area-inset-bottom"
                              style={{ borderColor: 'var(--color-border)' }}
                            >
                              <button
                                onClick={() => {
                                  setBellOpen(false);
                                  navigate(ROUTES.PROFILE);
                                }}
                                className="w-full py-2.5 rounded-lg bg-primary/10 text-primary text-sm font-medium active:bg-primary/20 transition-colors"
                              >
                                Vedi tutte le notifiche
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* ── Desktop: Dropdown ── */
                      <div
                        className="absolute right-0 top-full mt-2 w-80 rounded-lg shadow-xl border z-50 overflow-hidden"
                        style={{
                          backgroundColor: 'var(--color-card)',
                          borderColor: 'var(--color-border)',
                        }}
                      >
                        {/* Header */}
                        <div
                          className="flex items-center justify-between px-4 py-3 border-b"
                          style={{ borderColor: 'var(--color-border)' }}
                        >
                          <h3 className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>Notifiche</h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={() => markAllNotifRead()}
                              className="text-xs text-primary hover:underline"
                            >
                              Segna tutte lette
                            </button>
                          )}
                        </div>

                        {/* List */}
                        <div className="max-h-80 overflow-y-auto">
                          {notifLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : recentNotifications.length === 0 ? (
                            <div className="py-8 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                              <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                              Nessuna notifica
                            </div>
                          ) : (
                            recentNotifications.map((n) => (
                              <button
                                key={n.id}
                                onClick={() => handleNotifClick(n.id, n.read)}
                                className={cn(
                                  'w-full text-left px-4 py-3 border-b transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50',
                                  !n.read && 'bg-primary/5'
                                )}
                                style={{ borderColor: 'var(--color-border)' }}
                              >
                                <div className="flex items-start gap-3">
                                  {!n.read && (
                                    <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                                  )}
                                  <div className={cn('flex-1 min-w-0', n.read && 'ml-5')}>
                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
                                      {n.title}
                                    </p>
                                    <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                                      {n.body}
                                    </p>
                                    <p className="text-xs mt-1 opacity-50" style={{ color: 'var(--color-text-secondary)' }}>
                                      {timeAgo(n.sentAt)}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))
                          )}
                        </div>

                        {/* Footer - only show if there are notifications */}
                        {recentNotifications.length > 0 && (
                          <div
                            className="px-4 py-2.5 border-t text-center"
                            style={{ borderColor: 'var(--color-border)' }}
                          >
                            <button
                              onClick={() => {
                                setBellOpen(false);
                                navigate(ROUTES.PROFILE);
                              }}
                              className="text-xs text-primary hover:underline"
                            >
                              Vedi tutte le notifiche
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Cart Button */}
              <Link
                to={ROUTES.CART}
                data-tour="nav-cart"
                className="relative p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6" data-tour="page-content">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav
          data-tour="mobile-nav"
          className="fixed bottom-0 left-0 right-0 h-16 border-t z-40 safe-area-inset-bottom"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="flex h-full">
            {navItems.slice(0, 5).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-1',
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <div className="relative w-6 h-6">
                  {item.icon}
                  {item.path === ROUTES.FAVORITES && favoritesCount > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-wine-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {favoritesCount > 9 ? '9+' : favoritesCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      <OnboardingTour />
    </div>
  );
};
